/**
 * A custom element that renders a burger menu.
 * @module publica-burger
 * @description
 *
 * @property {string} [maxWidth=1023] The max width of the burger menu before the regular nav comes back in.
 */
export class Burger extends HTMLElement {
	content: Element | null | undefined;
	main: Element | null | undefined;
	footer: Element | null | undefined;
	trigger: Element | null | undefined;
	mobileSlot: Element | null | undefined;
	desktopSlot: Element | null | undefined;
	dialog: HTMLDialogElement;

	constructor() {
		super();

		const dialog = document.createElement("dialog");
		dialog.setAttribute("closedby", "closerequest");
		dialog.classList.add(
			"absolute",
			"bg-white",
			"inset-0",
			"min-h-0",
			"overflow-y-auto",
			"size-full",
			"transition-[opacity,translate]",
			"ease-out",
			"motion-safe:starting:open:opacity-0",
			"motion-safe:starting:open:-translate-y-full",
			"open:duration-250",
			"open:opacity-100",
			"open:translate-y-0",
		);

		this.dialog = dialog;
	}

	static observedAttributes() {
		return ["maxWidth"];
	}

	get maxWidth() {
		return Number.parseInt(this.getAttribute("max-width") || "1024", 10);
	}

	set maxWidth(value) {
		this.setAttribute("maxWidth", String(value));
	}

	connectedCallback() {
		this.content = document.querySelector("#content");
		this.main = this.content?.querySelector("main");
		this.footer = this.content?.querySelector("footer");
		this.trigger = this.querySelector("button[slot='trigger']");
		this.mobileSlot = this.querySelector("[slot='mobile']");
		this.desktopSlot = this.querySelector("[slot='desktop']");

		this.attachShadow({ mode: "open" });

		const template = document.createElement("template");

		template.innerHTML = `
			<div data-element="burger-root" part="base">
				<style></style>
				<slot name="trigger"></slot>
				<slot name="desktop"></slot>
			</div>
		`
			.replaceAll(/\s\s+/g, " ")
			.trim();

		this.shadowRoot?.append(template.content.cloneNode(true));

		if (this.mobileSlot != null) {
			this.dialog.replaceChildren(this.mobileSlot.cloneNode(true));
		}

		this.trigger?.addEventListener("click", () => {
			this.toggleDialog();
		});

		this.dialog.addEventListener("toggle", (event) => {
			if (event.newState === "open") {
				document.body.classList.add("overflow-hidden");
				this.main?.setAttribute("inert", "");
				this.footer?.setAttribute("inert", "");
				this.trigger?.setAttribute("aria-expanded", "true");
				this.dataset.open = "true";
			} else {
				document.body.classList.remove("overflow-hidden");
				this.main?.removeAttribute("inert");
				this.footer?.removeAttribute("inert");
				this.trigger?.setAttribute("aria-expanded", "false");
				delete this.dataset.open;
			}
		});

		this.handleResize({
			target: document.body,
			initial: true,
		});

		if ("ResizeObserver" in globalThis) {
			new ResizeObserver((entries) => {
				const target = entries[0]?.target;
				this.handleResize({
					target,
				});
			}).observe(document.body);
		}
	}

	handleResize({
		target,
		initial = false,
	}: {
		target: Element | undefined;
		initial?: boolean;
	}) {
		if (target == null) return;

		if (target.clientWidth <= this.maxWidth) {
			// Mobile
			if (!initial && this.dataset.enabled === "true") return; // Bail early if not changing state

			// Close megamenu dialog if open
			const megamenuDialog: HTMLDialogElement | null =
				document.querySelector("#megamenu-dialog");
			megamenuDialog?.close();

			// Insert dialog, show trigger, enable burger
			this.content?.append(this.dialog);
			this.trigger?.classList.remove("hidden");
			this.trigger?.classList.add("block");
			this.desktopSlot?.classList.remove("block");
			this.desktopSlot?.classList.add("hidden");
			this.dataset.enabled = "true";
		} else {
			// Desktop
			if (!initial && this.dataset.enabled !== "true") return; // Bail early if not changing state

			// Close and remove dialog, hide trigger, disable burger
			this.dialog.remove();
			this.trigger?.classList.remove("block");
			this.trigger?.classList.add("hidden");
			this.desktopSlot?.classList.remove("hidden");
			this.desktopSlot?.classList.add("block");
			if (this.dataset.open) {
				this.toggleDialog();
			}
			delete this.dataset.enabled;
		}
	}

	toggleDialog() {
		if (this.dialog.open) {
			this.dialog.close();
		} else {
			this.dialog.show();
		}
	}
}
